import { Box, VStack, Button, Text, Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerHeader, DrawerBody, Image, HStack, Collapse, Icon, Tooltip, useColorMode } from '@chakra-ui/react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { ChevronRight, ChevronDown } from 'lucide-react'
import { renderMenuIcon } from '@/lib/icons/menuIcons'
import { useEffect, useState, useMemo, useCallback, memo, useRef } from 'react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'

interface NavigationItemDef {
  name: string
  href: string
  icon: string
}

interface SidebarProps {
  items?: NavigationItemDef[]
  isOpen?: boolean
  onClose?: () => void
  collapsed?: boolean
}

// Separate memoized logo component to prevent re-renders
const SidebarLogo = memo(({ logoUrl, appName, collapsed = false, colorMode, bg }: { logoUrl: string; appName: string; collapsed?: boolean; colorMode?: string; bg?: string }) => {
  const isDarkMode = colorMode === 'dark'
  const logoSrc = collapsed 
    ? (isDarkMode ? '/assets/img/als-white-icon-32x32.png' : '/assets/img/als-icon-32x32.png')
    : (isDarkMode ? '/assets/img/als-white-logo.png' : '/assets/img/Actionlabs Horizontal logo.png')
  
  return (
    <Box
      pt={4}
      px={collapsed ? 3 : 4}
      pb={collapsed ? 2 : 4}
      flexShrink={0}
      bg={bg}
      position="sticky"
      top={0}
      zIndex={1}
    >
      <HStack spacing={collapsed ? 0 : 2} align="center" overflow="hidden" w="full" justifyContent={collapsed ? 'center' : 'flex-start'}>
        <Image
          key={logoSrc}
          src={logoSrc}
          alt="ALS Logo"
          height={collapsed ? '32px' : '32px'}
          width={collapsed ? '32px' : 'auto'}
          objectFit="contain"
          loading="eager"
          flexShrink={0}
          onError={(e) => {
            // Fallback if image fails to load
            const target = e.target as HTMLImageElement
            if (!collapsed && target.src !== '/assets/img/Actionlabs Horizontal logo 1.png') {
              target.src = '/assets/img/Actionlabs Horizontal logo 1.png'
            }
          }}
        />
        {!collapsed && appName ? (
          <Text
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            flex="1"
            minW={0}
            opacity={collapsed ? 0 : 1}
            transform={collapsed ? 'translateX(-6px)' : 'translateX(0)'}
            transition="opacity 260ms cubic-bezier(.2,.8,.2,1), transform 260ms cubic-bezier(.2,.8,.2,1)"
            style={{ willChange: 'opacity, transform' }}
          >
            | {appName}
          </Text>
        ) : null}
      </HStack>
    </Box>
  )
})

SidebarLogo.displayName = 'SidebarLogo'

function SidebarComponent({ items, isOpen = false, onClose, collapsed = false }: SidebarProps) {
  const location = useLocation()
  const pathname = location.pathname || '/'
  const navigate = useNavigate()
  const { colorMode } = useColorMode()

  // Only subscribe to user_routes to prevent unnecessary re-renders when other user properties change
  const userRoutes = useAuthStore((state) => state.user?.user_routes)
  const borderColor = useThemeColors().border
  const { settings: siteSettings } = useSiteSettings()

  // Initialize openGroups from localStorage to persist across re-renders
  // Use lazy initializer to only read from localStorage once on mount
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem('sidebar-open-groups')
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, boolean>
        return parsed
      }
    } catch (e) {
      // Ignore parse errors
    }
    return {}
  })

  // Track groups the user has manually closed (don't auto-open on route change)
  const manuallyClosedRef = useRef<Set<string>>(new Set())

  // Memoize database-driven values to prevent recalculation on every render
  // Extract values directly to prevent object reference changes
  const sidenavLogo = siteSettings?.sidenav_logo

  // Add cache busting query parameter to logo URL (only when URL changes)
  const logoUrl = useMemo(() => {
    const defaultLogo = colorMode === 'dark' 
      ? '/assets/img/als-white-logo.png'
      : '/assets/img/Actionlabs Horizontal logo.png'
    const baseUrl = sidenavLogo || defaultLogo
    // Add version for cache busting (static version that changes only when logo changes)
    // In development, use a hash of the URL; in production, use build time
    const version = import.meta.env.DEV
      ? `?v=${baseUrl.split('/').pop()?.replace(/[^a-zA-Z0-9]/g, '') || 'dev'}`
      : `?v=${import.meta.env.VITE_BUILD_TIME || '1.0.0'}`
    return baseUrl.includes('?') ? baseUrl : `${baseUrl}${version}`
  }, [sidenavLogo, colorMode])
  const appName = useMemo(() =>
    import.meta.env.VITE_APP_NAME || 'ADMIN PANEL',
    [] // Empty dependency array since env variable doesn't change
  )

  const colors = useThemeColors()
  const sidebarBg = colors.sidebarBg
  const sidebarText = colors.secondary
  const sidebarHoverBg = colors.sidebarHoverBg
  const activeAccent = colors.primary
  const sectionLabelColor = colors.textMuted
  const dividerColor = colors.borderLight

  const collapsedW = '72px'
  const expandedW = '240px'

  const navBtnProps = {
    variant: 'ghost' as const,
    w: '100%',
    size: 'sm' as const,
    borderRadius: 'md',
    px: 3,
  }

  // Memoize helper functions to prevent recreation on every render
  const joinPaths = useCallback((parent: string, child: string) => {
    const a = (parent || '').replace(/\/+$/, '')
    const b = (child || '').replace(/^\/+/, '')
    const joined = `${a}/${b}`
    return joined.startsWith('/') ? joined : `/${joined}`
  }, [])

  // Memoize routes to prevent unnecessary recalculations and re-renders
  const topLevelRoutes = useMemo(() => {
    const routes = Array.isArray(userRoutes) ? userRoutes : []

    // Debug logging to help diagnose navigation issues
    if (routes.length === 0 && userRoutes) {
      console.warn('[Sidebar] No routes found. userRoutes:', userRoutes)
    } else if (routes.length > 0) {
      console.log('[Sidebar] Total routes:', routes.length, 'Routes:', routes)
    }

    const filtered = routes.filter((r: any) => {
      // A parent route is included if its own side_nav is 'true'
      // OR if it has at least one child with side_nav 'true'
      const hasDisplayableChildren = Array.isArray(r.children) && r.children.some((c: any) => c.side_nav === 'true')
      return r.side_nav === 'true' || hasDisplayableChildren
    })

    if (filtered.length === 0 && routes.length > 0) {
      console.warn('[Sidebar] No routes with side_nav=true. All routes:', routes.map((r: any) => ({
        name: r.name,
        path: r.path,
        side_nav: r.side_nav
      })))
    }

    return filtered
  }, [userRoutes])

  const isActivePath = useCallback((targetPath: string) => {
    const norm = (p: string) => ('/' + (p || '').replace(/^\/+/, '')).replace(/\/+$/, '')
    const cur = norm(pathname)
    const tgt = norm(targetPath)
    return cur === tgt || cur.startsWith(tgt + '/')
  }, [pathname])

  useEffect(() => {
    // Auto-open the parent whose child is active (accordion: only one group open)
    // Respect manually closed groups (don't auto-open them even if they have active children)
    const groupsToOpen: Record<string, boolean> = {}
    topLevelRoutes.forEach((route: any) => {
      const groupKey = route.path || route.name
      if (manuallyClosedRef.current.has(groupKey)) return
      if (Array.isArray(route.children)) {
        const shouldOpen = route.children.some((c: any) => isActivePath(joinPaths(route.path, c.path)))
        if (shouldOpen || isActivePath(route.path)) groupsToOpen[groupKey] = true
      } else if (isActivePath(route.path)) {
        groupsToOpen[groupKey] = true
      }
    })

    // Accordion: at most one group open; the one containing the active route, or none
    const keysToOpen = Object.keys(groupsToOpen)
    const soleKey = keysToOpen.length > 0 ? keysToOpen[0] : null
    const next = soleKey ? { [soleKey]: true } : {}

    setOpenGroups((prev) => {
      const prevOpen = Object.keys(prev).find((k) => prev[k])
      if ((soleKey || null) === (prevOpen || null)) return prev
      return next
    })
  }, [pathname, topLevelRoutes, isActivePath, joinPaths])

  // Helper to check if parent has active child
  const hasActiveChild = useCallback((route: any) => {
    if (!Array.isArray(route.children)) return false
    return route.children.some((c: any) => {
      if (c.side_nav !== 'true') return false
      const childPath = joinPaths(route.path, c.path)
      return isActivePath(childPath)
    })
  }, [isActivePath, joinPaths])

  // Memoize renderNavButtons to prevent recreation
  const renderNavButtons = useCallback((routes: any[], forceExpanded: boolean = false) => {
    const isCollapsed = collapsed && !forceExpanded
    return routes.map((route: any) => {
      const displayableChildren = Array.isArray(route.children) ? route.children.filter((c: any) => c.side_nav === 'true') : []
      const hasDisplayableChildren = displayableChildren.length > 0

      // Collapsed mode: show icons-only with tooltips, clicking navigates to the primary route
      if (isCollapsed) {
        // If parent has children, navigate to first child if present
        const target = hasDisplayableChildren && displayableChildren[0]
          ? (displayableChildren[0].path?.startsWith('/') ? displayableChildren[0].path : joinPaths(route.path, displayableChildren[0].path))
          : route.path

        const btn = (
          <Button
            key={route.path}
            {...navBtnProps}
            onClick={(e) => {
              e.preventDefault()
              navigate(target)
              onClose?.()
            }}
            color={isActivePath(route.path) ? activeAccent : sidebarText}
            bg="transparent"
            _hover={{ bg: sidebarHoverBg }}
            justifyContent="center"
            px={2}
          >
            {renderMenuIcon(route.icon)}
          </Button>
        )

        return (
          <Tooltip key={route.path} label={route.name} placement="right" hasArrow>
            <Box>{btn}</Box>
          </Tooltip>
        )
      }

      // Expanded mode: render full label buttons and collapsible groups
      if (route.side_nav === 'true' && !hasDisplayableChildren) {
        const isRouteActive = isActivePath(route.path)

        const btn = (
          <Button
            key={route.path}
            {...navBtnProps}
            onClick={(e) => {
              e.preventDefault()
              navigate(route.path)
              onClose?.()
            }}
            color={isRouteActive ? activeAccent : sidebarText}
            bg="transparent"
            _hover={{ bg: sidebarHoverBg }}
            justifyContent="flex-start"
          >
            <HStack spacing={3}>
              {renderMenuIcon(route.icon)}
              <Text>{route.name}</Text>
            </HStack>
          </Button>
        )

        return btn
      } else if (hasDisplayableChildren) {
        // Render as a collapsible group
        const groupKey = route.path || route.name
        const isOpen = !!openGroups[groupKey]
        // Parent is active if the parent path is active OR if it has an active child
        const isParentActive = isActivePath(route.path) || hasActiveChild(route)

        // Get first child path for navigation when parent is clicked
        // Child paths from backend are already absolute (e.g., /access-control/users)
        // So we use the child path directly if it's absolute, otherwise join with parent
        const firstChild = displayableChildren.length > 0 ? displayableChildren[0] : null
        const firstChildPath = firstChild
          ? (firstChild.path?.startsWith('/')
            ? firstChild.path
            : joinPaths(route.path, firstChild.path))
          : null

        const parentBtn = (
          <Button
            key={route.path}
            {...navBtnProps}
            onClick={(e) => {
              e.preventDefault()
              setOpenGroups((prev) => {
                const newState = !prev[groupKey]
                if (newState) {
                  manuallyClosedRef.current.delete(groupKey)
                  const updated = { [groupKey]: true }
                  try {
                    localStorage.setItem('sidebar-open-groups', JSON.stringify(updated))
                  } catch {
                    //
                  }
                  if (firstChildPath) navigate(firstChildPath)
                  return updated
                } else {
                  manuallyClosedRef.current.add(groupKey)
                  const updated = { ...prev, [groupKey]: false }
                  try {
                    localStorage.setItem('sidebar-open-groups', JSON.stringify(updated))
                  } catch {
                    //
                  }
                  return updated
                }
              })
            }}
            color={isParentActive ? activeAccent : sidebarText}
            bg="transparent"
            _hover={{ bg: sidebarHoverBg }}
            justifyContent="space-between"
          >
            {isCollapsed ? (
              renderMenuIcon(route.icon)
            ) : (
              <HStack w="full" justify="space-between" spacing={3}>
                <HStack spacing={3}>
                  {renderMenuIcon(route.icon)}
                  <Text>{route.name}</Text>
                </HStack>
                <Icon as={isOpen ? ChevronDown : ChevronRight} />
              </HStack>
            )}
          </Button>
        )

        return (
          <Box key={route.path}>
            {isCollapsed ? (
              <Tooltip label={route.name} placement="right" hasArrow>
                <Box>{parentBtn}</Box>
              </Tooltip>
            ) : (
              parentBtn
            )}

            {!isCollapsed && (
              <Collapse in={isOpen} animateOpacity>
                <VStack spacing={1} pl={6} align="stretch" mt={1}>
                  {displayableChildren.map((child: any) => {
                    const childPath = joinPaths(route.path, child.path)
                    const isChildActive = isActivePath(childPath)

                    return (
                      <Button
                        key={child.path}
                        {...navBtnProps}
                        size="sm"
                        pl={6}
                        onClick={(e) => {
                          e.preventDefault()
                          navigate(childPath)
                          onClose?.()
                        }}
                        color={isChildActive ? activeAccent : sidebarText}
                        bg="transparent"
                        _hover={{ bg: sidebarHoverBg }}
                        justifyContent="flex-start"
                      >
                        <HStack spacing={3}>
                          {renderMenuIcon(child.icon)}
                          <Text>{child.name}</Text>
                        </HStack>
                      </Button>
                    )
                  })}
                </VStack>
              </Collapse>
            )}
          </Box>
        )
      }
      return null // Should not happen if filtered correctly
    })
  }, [openGroups, isActivePath, hasActiveChild, joinPaths, navigate, sidebarText, sidebarHoverBg, activeAccent, collapsed, onClose, navBtnProps])

  const fallbackItems = useMemo(() => (items || []).map(it => (
    <Button
      key={it.href}
      {...navBtnProps}
      onClick={(e) => {
        e.preventDefault()
        navigate(it.href)
        onClose?.()
      }}
      color={sidebarText}
      bg="transparent"
      _hover={{ bg: sidebarHoverBg }}
      justifyContent="flex-start"
    >
      <HStack spacing={3}>
        {renderMenuIcon(it.icon)}
        <Text>{it.name}</Text>
      </HStack>
    </Button>
  )), [items, navigate, sidebarText, sidebarHoverBg, navBtnProps, onClose])

  const desktopWidth = collapsed ? 14 : 64

  return (
    <>
      {/* Desktop sidebar */}
      <Box
        w={{ base: 0, md: desktopWidth }}
        minW={{ base: 0, md: desktopWidth }}
        transition="width 280ms cubic-bezier(.2,.8,.2,1), padding 260ms cubic-bezier(.2,.8,.2,1)"
        sx={{ willChange: 'width, padding' }}
        bg={sidebarBg}
        borderRight="1px"
        borderColor={borderColor}
        h="100vh"
        maxH="100vh"
        display={{ base: 'none', md: 'flex' }}
        flexDirection="column"
        color={sidebarText}
        overflow="hidden"
      >
        <Box flex="1" display="flex" flexDirection="column" overflow="hidden">
          {/* Fixed Logo Section - Memoized to prevent re-renders */}
          <SidebarLogo logoUrl={logoUrl} appName={appName} collapsed={collapsed} colorMode={colorMode} bg={sidebarBg} />

          {/* Scrollable Navigation Section */}
          <VStack
            spacing={1}
            align="stretch"
            px={collapsed ? 2 : 3}
            pt={collapsed ? 0.5 : 1}
            pb={collapsed ? 2 : 4}
            flex="1"
            overflowY="auto"
            overflowX="hidden"
            css={{
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(148, 163, 184, 0.35)',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'rgba(148, 163, 184, 0.6)',
              },
            }}
          >
            {topLevelRoutes.length > 0 ? renderNavButtons(topLevelRoutes) : fallbackItems}
          </VStack>
        </Box>
      </Box>

      {/* Mobile drawer */}
      <Drawer isOpen={!!isOpen} placement="left" onClose={onClose || (() => { })}>
        <DrawerOverlay />
        <DrawerContent bg={sidebarBg} color={sidebarText}>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" borderColor={dividerColor}>
            <HStack spacing={2} align="center" justify="center" overflow="hidden" w="full">
              <Image src={logoUrl} alt="ALS Logo" height="24px" objectFit="contain" flexShrink={0} />
              {appName ? (
                <Text
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  flex="1"
                  minW={0}
                >
                  | {appName}
                </Text>
              ) : null}
            </HStack>
          </DrawerHeader>
          <DrawerBody>
            <VStack spacing={4} align="stretch">
              {topLevelRoutes.length > 0 ? renderNavButtons(topLevelRoutes, true) : fallbackItems}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  )
}

// Memoize the component to prevent re-renders when props haven't changed
export const Sidebar = memo(SidebarComponent, (prevProps, nextProps) => {
  // Only re-render if isOpen, onClose, collapsed, or items actually change
  // Use deep comparison for items array to prevent unnecessary re-renders
  let itemsEqual = prevProps.items === nextProps.items
  if (!itemsEqual && prevProps.items && nextProps.items) {
    const prevItems = prevProps.items
    const nextItems = nextProps.items
    itemsEqual = prevItems.length === nextItems.length &&
      prevItems.every((item, idx) => {
        const nextItem = nextItems[idx]
        return nextItem && item.href === nextItem.href &&
          item.name === nextItem.name &&
          item.icon === nextItem.icon
      })
  }

  return prevProps.isOpen === nextProps.isOpen &&
    prevProps.onClose === nextProps.onClose &&
    prevProps.collapsed === nextProps.collapsed &&
    itemsEqual
})

export default Sidebar

